import { useMemo, useRef } from 'react';

import { Box, Container } from '@frontierag/frontier-ui-library';
import { Header, OfflineNotification, SubHeader } from 'components';
import { BoxLoader } from 'shared';
import { Helmet } from 'react-helmet';

import PageLayoutProvider from './PageLayoutProvider';
import type { IPageLayout } from './types';
import { useApp } from 'contexts/App';
import { useTranslation } from 'react-i18next';

const PageLayout = ({
  title,
  sx,
  children,
  loading,
  notification,
  enableLayoutContext = false,
  watermark,
}: IPageLayout) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const {
    state: { applicationType, userSettings },
  } = useApp();
  const { t } = useTranslation();

  const { isAdmin = false } = userSettings || {};
  const applicationTypeIsSet = Boolean(applicationType);
  const shouldDisplayMenu = !applicationTypeIsSet && isAdmin ? true : applicationTypeIsSet;

  const pageContent = useMemo(
    () => (
      <>
        <Container maxWidth="lg" disableGutters data-testid="offline-notification-container">
          <OfflineNotification />
          {notification && (
            <Box pt={2} px={2}>
              {notification}
            </Box>
          )}
        </Container>
        {title && (
          <>
            <SubHeader title={title} />
            <Helmet>
              <title>
                {title} -{' '}
                {applicationType ? t(`meta.titles.${applicationType}`) : t('meta.titles.default')}
              </title>
            </Helmet>
          </>
        )}

        <Container
          maxWidth="lg"
          sx={[{ pt: 3, pb: 4, flexGrow: 1, position: 'relative' }]}
          disableGutters
          data-testid="body-content"
        >
          {loading ? (
            <BoxLoader centered />
          ) : (
            <>
              {watermark}
              {children}
            </>
          )}
        </Container>
      </>
    ),
    [notification, title, applicationType, t, loading, watermark, children],
  );

  return (
    <Box height="100vh" position="relative" display="flex" flexDirection="column">
      <Header
        showMenu={shouldDisplayMenu}
        subtitle={applicationType ? t(`Header:subtitles.${applicationType}`) : ''}
      />
      <Box
        sx={[
          {
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            overflowY: 'auto',
            height: 1,
            scrollBehavior: 'smooth',
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        ref={ref}
      >
        {enableLayoutContext ? (
          <PageLayoutProvider containerRef={ref}>{pageContent}</PageLayoutProvider>
        ) : (
          pageContent
        )}
      </Box>
    </Box>
  );
};

export default PageLayout;
